import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { ArrowLeftIcon, PlusIcon } from '@heroicons/react/24/outline';
import { quoteService } from '../services/quoteService';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const ProductManager = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);

  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    icon_name: 'fas fa-box'
  });

  const [productForm, setProductForm] = useState({
    category_id: '',
    name: '',
    description: '',
    base_price: '',
    unit_name: 'item',
    icon_name: 'fas fa-plus-circle',
    pricing_tiers: [
      { min_quantity: 1, max_quantity: 4, price_per_unit: '', discount_percentage: 0 },
      { min_quantity: 5, max_quantity: 9, price_per_unit: '', discount_percentage: 10 },
      { min_quantity: 10, max_quantity: null, price_per_unit: '', discount_percentage: 20 }
    ]
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      console.log('📊 LOADING: Starting to load categories and products for user:', user?.id);
      
      const [catRes, prodRes] = await Promise.all([
        quoteService.getProductCategories(user?.id),
        quoteService.getInteractiveProducts(user?.id)
      ]);
      
      console.log('📊 LOADING: Categories response:', catRes);
      console.log('📊 LOADING: Products response:', prodRes);
      
      setCategories(catRes.data || []);
      setProducts(prodRes.data || []);
      
      console.log('📊 LOADING: Set', catRes.data?.length || 0, 'categories and', prodRes.data?.length || 0, 'products');
    } catch (err) {
      console.error('❌ LOADING: Failed to load data:', err);
      toast.error('Failed to load products: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async e => {
    e.preventDefault();
    try {
      await quoteService.createProductCategory(categoryForm);
      toast.success('Category created!');
      setCategoryForm({ name: '', description: '', icon_name: 'fas fa-box' });
      setShowCategoryForm(false);
      loadData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleCreateProduct = async e => {
    e.preventDefault();
    try {
      console.log('📦 PRODUCT: Creating new interactive product...');
      
      const base = parseFloat(productForm.base_price);
      
      // Extract pricing_tiers from productForm before sending to database
      const { pricing_tiers, ...productDataWithoutTiers } = productForm;
      
      console.log('📄 PRODUCT: Product data (without tiers):', productDataWithoutTiers);
      console.log('🏷️ PRODUCT: Pricing tiers to create:', pricing_tiers);
      
      // Create the product without pricing_tiers
      const prodRes = await quoteService.createInteractiveProduct({
        ...productDataWithoutTiers,
        base_price: base
      });
      
      console.log('✅ PRODUCT: Product created with ID:', prodRes.data.id);
      
      // Create pricing tiers separately
      const tiers = pricing_tiers.map(t => ({
        ...t,
        price_per_unit: t.price_per_unit || (base * (1 - t.discount_percentage / 100))
      }));
      
      console.log('📈 PRODUCT: Creating pricing tiers:', tiers);
      await quoteService.createPricingTiers(prodRes.data.id, tiers);
      
      console.log('✅ PRODUCT: Pricing tiers created successfully');
      
      toast.success('Product created!');
      setShowProductForm(false);
      loadData();
    } catch (err) {
      console.error('❌ PRODUCT: Creation failed:', err);
      toast.error(err.message);
    }
  };

  const handleDeleteProduct = async (productId, productName) => {
    if (!window.confirm(`Are you sure you want to delete "${productName}"? This action cannot be undone and will also delete all associated pricing tiers.`)) {
      return;
    }

    try {
      await quoteService.deleteInteractiveProduct(productId);
      toast.success(`"${productName}" deleted successfully`);
      loadData(); // Reload the data to update the UI
    } catch (error) {
      toast.error(`Failed to delete product: ${error.message}`);
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    const productsInCategory = products.filter(p => p.category_id === categoryId);
    
    if (productsInCategory.length > 0) {
      if (!window.confirm(
        `"${categoryName}" contains ${productsInCategory.length} product(s). ` +
        `Deleting this category will also delete all products in it. Are you sure?`
      )) {
        return;
      }
    } else {
      if (!window.confirm(`Are you sure you want to delete the "${categoryName}" category?`)) {
        return;
      }
    }

    try {
      await quoteService.deleteProductCategory(categoryId);
      toast.success(`"${categoryName}" category deleted successfully`);
      loadData(); // Reload the data to update the UI
    } catch (error) {
      toast.error(`Failed to delete category: ${error.message}`);
    }
  };

  const updatePricingTier = (idx, field, val) => {
    const tiers = [...productForm.pricing_tiers];
    tiers[idx][field] = val;
    
    // Smart auto-correction to prevent logical errors
    if (field === 'min_quantity') {
      const minVal = parseInt(val) || 1;
      tiers[idx].min_quantity = minVal;
      
      // Auto-update previous tier's max_quantity
      if (idx > 0 && minVal > 1) {
        tiers[idx - 1].max_quantity = minVal - 1;
      }
      
      // Auto-update current tier's max_quantity if it's invalid
      if (tiers[idx].max_quantity && tiers[idx].max_quantity < minVal) {
        tiers[idx].max_quantity = minVal + 4; // Default to +4 range
      }
    }
    
    if (field === 'max_quantity') {
      const maxVal = val ? parseInt(val) : null;
      tiers[idx].max_quantity = maxVal;
      
      // Auto-update next tier's min_quantity if it exists
      if (maxVal && idx < tiers.length - 1) {
        tiers[idx + 1].min_quantity = maxVal + 1;
      }
    }
    
    // Recalculate price based on discount
    if (field === 'discount_percentage' && productForm.base_price) {
      tiers[idx].price_per_unit = (
        parseFloat(productForm.base_price) * (1 - val / 100)
      ).toFixed(2);
    }
    
    setProductForm({ ...productForm, pricing_tiers: tiers });
  };

  const iconOptions = [
    { value: 'fas fa-box', label: 'Box' },
    { value: 'fas fa-tools', label: 'Tools' },
    { value: 'fas fa-crosshairs', label: 'Target' },
    { value: 'fas fa-calendar-check', label: 'Calendar' },
    { value: 'fas fa-file-alt', label: 'Document' },
    { value: 'fas fa-shield-alt', label: 'Shield' },
    { value: 'fas fa-cog', label: 'Settings' },
    { value: 'fas fa-plus-circle', label: 'Plus' },
    { value: 'fas fa-star', label: 'Star' },
    { value: 'fas fa-bolt', label: 'Lightning' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center font-jetbrains">
        <div className="flex items-center space-x-3">
          <div className="spinner spinner-sm" />
          <span className="text-lg text-black">Loading products...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-jetbrains text-black">
      {/* Header */}
      <div className="border-b border-black">
        <div className="max-w-7xl mx-auto px-6 py-8 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button onClick={() => navigate('/dashboard')} className="flex items-center space-x-2">
              <ArrowLeftIcon className="w-5 h-5" />
              <span className="font-instrument text-xl">Back to Dashboard</span>
            </button>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowCategoryForm(true)}
              className="flex items-center space-x-2 px-4 py-2 border border-black rounded-lg hover:bg-black hover:text-white transition"
            >
              <PlusIcon className="w-5 h-5" />
              <span>Add Category</span>
            </button>
            <button
              onClick={() => setShowProductForm(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition"
            >
              <PlusIcon className="w-5 h-5" />
              <span>Add Product</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12 space-y-12">
        <h1 className="font-instrument text-5xl tracking-tight">Interactive Products</h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Categories */}
          <section className="border border-black rounded-2xl p-6">
            <h2 className="font-instrument text-3xl tracking-tight mb-4">Categories</h2>
            {categories.length === 0 ? (
              <div className="text-center py-10">
                <i className="fas fa-box text-5xl text-gray-300 mb-4"></i>
                <p className="text-gray-500">No categories yet</p>
                <button
                  onClick={() => setShowCategoryForm(true)}
                  className="mt-4 underline hover:text-black"
                >
                  Create first category
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {categories.map(c => (
                  <div key={c.id} className="border border-black rounded-xl p-4 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <i className={`${c.icon_name} text-2xl`} />
                      <div>
                        <p className="font-semibold">{c.name}</p>
                        <p className="text-sm text-gray-500">{c.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-semibold">
                        {products.filter(p => p.category_id === c.id).length} products
                      </span>
                      <button
                        onClick={() => handleDeleteCategory(c.id, c.name)}
                        className="text-red-600 hover:text-red-800 hover:bg-red-50 p-2 rounded-lg transition-colors"
                        title={`Delete ${c.name} category`}
                      >
                        <i className="fas fa-trash text-sm"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Products */}
          <section className="border border-black rounded-2xl p-6">
            <h2 className="font-instrument text-3xl tracking-tight mb-4">Products</h2>
            {products.length === 0 ? (
              <div className="text-center py-10">
                <i className="fas fa-plus-circle text-5xl text-gray-300 mb-4"></i>
                <p className="text-gray-500">No products yet</p>
                <button
                  onClick={() => setShowProductForm(true)}
                  className="mt-4 underline hover:text-black"
                >
                  Create first product
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {products.map(p => (
                  <div key={p.id} className="border border-black rounded-xl p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        <i className={`${p.icon_name} text-2xl text-black`} />
                        <div className="flex-1">
                          <p className="font-semibold">{p.name}</p>
                          <p className="text-sm text-gray-500">{p.description}</p>
                          <div className="mt-2 space-y-1 text-sm">
                            <span>
                              Base ${p.base_price} / {p.unit_name}
                            </span>
                            {p.pricing_tiers?.length > 0 && (
                              <ul className="text-xs text-gray-600 list-disc list-inside">
                                {p.pricing_tiers.map((t, i) => (
                                  <li key={i}>
                                    {t.min_quantity}
                                    {t.max_quantity ? `-${t.max_quantity}` : '+'}{' '}
                                    {p.unit_name} – ${t.price_per_unit} each
                                    {t.discount_percentage > 0 && (
                                      <span className="ml-1 text-green-600">
                                        (-{t.discount_percentage}%)
                                      </span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteProduct(p.id, p.name)}
                        className="text-red-600 hover:text-red-800 hover:bg-red-50 p-2 rounded-lg transition-colors ml-3"
                        title={`Delete ${p.name} product`}
                      >
                        <i className="fas fa-trash text-sm"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>

      {/* Category Modal */}
      {showCategoryForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 font-jetbrains">
          <div className="bg-white border border-black rounded-2xl p-6 w-full max-w-md mx-4">
            <h3 className="font-instrument text-2xl tracking-tight mb-4">Add Category</h3>
            <form onSubmit={handleCreateCategory} className="space-y-4">
              <input
                required
                placeholder="Category name"
                value={categoryForm.name}
                onChange={e => setCategoryForm({ ...categoryForm, name: e.target.value })}
                className="w-full px-4 py-2 border border-black rounded-lg"
              />
              <textarea
                placeholder="Description"
                value={categoryForm.description}
                onChange={e => setCategoryForm({ ...categoryForm, description: e.target.value })}
                className="w-full px-4 py-2 border border-black rounded-lg"
                rows={2}
              />
              <select
                value={categoryForm.icon_name}
                onChange={e => setCategoryForm({ ...categoryForm, icon_name: e.target.value })}
                className="w-full px-4 py-2 border border-black rounded-lg"
              >
                {iconOptions.map(i => (
                  <option key={i.value} value={i.value}>{i.label}</option>
                ))}
              </select>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCategoryForm(false)}
                  className="px-4 py-2 border border-black rounded-lg hover:bg-black hover:text-white transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Product Modal */}
      {showProductForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 font-jetbrains">
          <div className="bg-white border border-black rounded-2xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="font-instrument text-2xl tracking-tight mb-4">Add Product</h3>
            <form onSubmit={handleCreateProduct} className="space-y-4">
              <select
                required
                value={productForm.category_id}
                onChange={e => setProductForm({ ...productForm, category_id: e.target.value })}
                className="w-full px-4 py-2 border border-black rounded-lg"
              >
                <option value="">Select category</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <input
                required
                placeholder="Product name"
                value={productForm.name}
                onChange={e => setProductForm({ ...productForm, name: e.target.value })}
                className="w-full px-4 py-2 border border-black rounded-lg"
              />
              <textarea
                placeholder="Description"
                value={productForm.description}
                onChange={e => setProductForm({ ...productForm, description: e.target.value })}
                className="w-full px-4 py-2 border border-black rounded-lg"
                rows={2}
              />
              <div className="grid grid-cols-2 gap-4">
                <input
                  required
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Base price"
                  value={productForm.base_price}
                  onChange={e => setProductForm({ ...productForm, base_price: e.target.value })}
                  className="w-full px-4 py-2 border border-black rounded-lg"
                />
                <input
                  required
                  placeholder="Unit name"
                  value={productForm.unit_name}
                  onChange={e => setProductForm({ ...productForm, unit_name: e.target.value })}
                  className="w-full px-4 py-2 border border-black rounded-lg"
                />
              </div>
              <select
                value={productForm.icon_name}
                onChange={e => setProductForm({ ...productForm, icon_name: e.target.value })}
                className="w-full px-4 py-2 border border-black rounded-lg"
              >
                {iconOptions.map(i => (
                  <option key={i.value} value={i.value}>{i.label}</option>
                ))}
              </select>

              {/* Pricing Tiers - IMPROVED WITH EXPLANATIONS */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <div className="mb-4">
                  <h4 className="font-instrument text-xl font-semibold mb-2 text-black">Bulk Pricing Tiers</h4>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start space-x-2">
                      <i className="fas fa-info-circle text-blue-600 mt-0.5"></i>
                      <div className="text-sm text-blue-800">
                        <p className="font-semibold mb-1">What are pricing tiers?</p>
                        <p className="mb-2">Encourage customers to buy more by offering discounts for larger quantities. This is the "McDonald's upsell" psychology - the more they buy, the better deal they get!</p>
                        <p className="font-semibold text-blue-900">Example: Pest Control Traps</p>
                        <ul className="text-xs text-blue-700 mt-1">
                          <li>• 1-4 traps = $25 each (full price)</li>
                          <li>• 5-9 traps = $22.50 each (10% discount)</li>
                          <li>• 10+ traps = $20 each (20% discount)</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-4 gap-3 text-sm font-semibold text-gray-700">
                    <span>Min Quantity</span>
                    <span>Max Quantity</span>
                    <span>Discount %</span>
                    <span>Final Price</span>
                  </div>
                  
                  {productForm.pricing_tiers.map((tier, i) => {
                    const basePrice = parseFloat(productForm.base_price) || 0;
                    const finalPrice = basePrice * (1 - tier.discount_percentage / 100);
                    const isDiscounted = tier.discount_percentage > 0;
                    
                    // Validation checks
                    const isValidRange = !tier.max_quantity || tier.min_quantity <= tier.max_quantity;
                    const prevTier = i > 0 ? productForm.pricing_tiers[i - 1] : null;
                    const hasGapFromPrevious = prevTier && prevTier.max_quantity && (tier.min_quantity !== prevTier.max_quantity + 1);
                    const hasInvalidOrder = !isValidRange;
                    
                    const hasError = hasInvalidOrder || hasGapFromPrevious;
                    
                    return (
                      <div key={i} className={`grid grid-cols-4 gap-3 items-center p-2 rounded-lg ${
                        hasError ? 'bg-red-50 border border-red-200' : 'bg-white'
                      }`}>
                        <div className="relative">
                          <input
                            type="number"
                            min="1"
                            placeholder="1"
                            value={tier.min_quantity}
                            onChange={e => updatePricingTier(i, 'min_quantity', parseInt(e.target.value) || 1)}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none ${
                              hasError ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-black'
                            }`}
                          />
                          {i === 0 && (
                            <div className="absolute -bottom-5 left-0 text-xs text-gray-500">
                              Starts at 1
                            </div>
                          )}
                        </div>
                        <div className="relative">
                          <input
                            type="number"
                            placeholder={i === productForm.pricing_tiers.length - 1 ? "Leave empty" : "Auto-filled"}
                            value={tier.max_quantity || ''}
                            onChange={e =>
                              updatePricingTier(i, 'max_quantity', e.target.value ? parseInt(e.target.value) : null)
                            }
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none ${
                              hasError ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-black'
                            }`}
                          />
                          {i === productForm.pricing_tiers.length - 1 && !tier.max_quantity && (
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm pointer-events-none">
                              ∞
                            </div>
                          )}
                          {i === productForm.pricing_tiers.length - 1 && (
                            <div className="absolute -bottom-5 left-0 text-xs text-gray-500">
                              No upper limit
                            </div>
                          )}
                        </div>
                        <div className="relative">
                          <input
                            type="number"
                            min="0"
                            max="50"
                            step="5"
                            placeholder="0"
                            value={tier.discount_percentage}
                            onChange={e => updatePricingTier(i, 'discount_percentage', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-black focus:outline-none"
                          />
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm pointer-events-none">
                            %
                          </div>
                          {i === 0 && (
                            <div className="absolute -bottom-5 left-0 text-xs text-gray-500">
                              Usually 0%
                            </div>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`font-semibold ${
                            isDiscounted ? 'text-green-600' : 'text-gray-900'
                          }`}>
                            ${finalPrice.toFixed(2)}
                          </span>
                          {isDiscounted && (
                            <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                              Save ${(basePrice - finalPrice).toFixed(2)}
                            </span>
                          )}
                          {hasError && (
                            <span className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded-full">
                              ⚠️ Error
                            </span>
                          )}
                        </div>
                        
                        {/* Error messages */}
                        {hasError && (
                          <div className="col-span-4 text-xs text-red-600 bg-red-50 p-2 rounded">
                            {hasInvalidOrder && (
                              <div>• Min quantity ({tier.min_quantity}) cannot be greater than max quantity ({tier.max_quantity})</div>
                            )}
                            {hasGapFromPrevious && (
                              <div>• Gap detected: This tier should start at {(prevTier?.max_quantity || 0) + 1}</div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <i className="fas fa-lightbulb text-yellow-600 mt-0.5"></i>
                    <div className="text-sm text-yellow-800">
                      <p className="font-semibold mb-1">💡 Pro Tips for Maximum Sales:</p>
                      <ul className="text-xs space-y-1">
                        <li>• Start with 0% discount for small quantities (maintains your profit margins)</li>
                        <li>• Offer 10-20% discounts for medium quantities (sweet spot for customers)</li>
                        <li>• Make the highest tier open-ended (10+ items) to encourage large orders</li>
                        <li>• Test different discount levels - even 5% can increase order sizes significantly!</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                {productForm.base_price && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="text-sm text-green-800">
                      <p className="font-semibold mb-2">📊 Your Pricing Preview:</p>
                      <div className="space-y-1 text-xs">
                        {productForm.pricing_tiers.map((tier, i) => {
                          const basePrice = parseFloat(productForm.base_price);
                          const finalPrice = basePrice * (1 - tier.discount_percentage / 100);
                          const rangeText = tier.max_quantity 
                            ? `${tier.min_quantity}-${tier.max_quantity}` 
                            : `${tier.min_quantity}+`;
                          
                          return (
                            <div key={i} className="flex justify-between">
                              <span>{rangeText} {productForm.unit_name || 'items'}:</span>
                              <span className="font-semibold">
                                ${finalPrice.toFixed(2)} each
                                {tier.discount_percentage > 0 && (
                                  <span className="text-green-600 ml-1">(-{tier.discount_percentage}%)</span>
                                )}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowProductForm(false)}
                  className="px-4 py-2 border border-black rounded-lg hover:bg-black hover:text-white transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductManager;
